// StorageOrphansAdmin — Painel de gerenciamento de arquivos órfãos.
//
// O Emergent Object Storage não expõe endpoint de exclusão. Este painel:
//  - Lista órfãos registrados com auditoria (path, tipo, origem, motivo, data)
//  - Executa dry-run (simulação) antes da purga real
//  - Executa purga real (que hoje marca "acknowledged" — no-op físico)
//  - Reconhece manualmente órfãos individuais
//  - Banner explícito sobre a limitação atual + estratégia de migração

import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { HardDrive, Play, AlertTriangle, CheckCircle, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_LABELS = {
  pending: "Pendente de remoção física",
  acknowledged: "Reconhecido (não removido)",
  purged: "Removido fisicamente",
  failed: "Falhou",
};

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  acknowledged: "bg-sky-100 text-sky-700",
  purged: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
};

export default function StorageOrphansAdmin() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/storage/orphans/summary"),
        api.get(`/admin/storage/orphans${statusFilter ? `?status=${statusFilter}` : ""}`),
      ]);
      setSummary(s.data);
      setItems(Array.isArray(l.data) ? l.data : []);
    } catch {
      toast.error("Falha ao carregar órfãos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const runPurge = async (dry_run) => {
    setPurging(true);
    try {
      const r = await api.post(`/admin/storage/orphans/purge?dry_run=${dry_run}`);
      setLastResult({ ...r.data, ranAt: new Date().toLocaleString("pt-BR") });
      toast.success(
        dry_run
          ? "Simulação concluída. Nenhum arquivo foi tocado."
          : "Limpeza concluída. Enquanto o storage não expõe delete, os registros ficam como reconhecidos (sem remoção física)."
      );
      await load();
    } catch {
      toast.error("Falha ao executar");
    } finally {
      setPurging(false);
    }
  };

  const acknowledgeOne = async (id) => {
    try {
      await api.post(`/admin/storage/orphans/${id}/acknowledge`);
      toast.success("Órfão reconhecido.");
      await load();
    } catch {
      toast.error("Falha ao reconhecer");
    }
  };

  return (
    <section className="space-y-6" data-testid="admin-storage-orphans">
      <header>
        <div className="flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-gold" />
          <h2 className="font-display text-2xl text-navy">Arquivos Órfãos no Storage</h2>
        </div>
        <p className="text-sm text-navy/65 mt-1 max-w-3xl">
          Registro auditável de arquivos que deixaram de ser referenciados no MongoDB.
          Cada substituição de arquivo, exclusão de vídeo ou upload cancelado gera um registro aqui.
        </p>
      </header>

      {/* Banner de limitação técnica — obrigatório para transparência editorial. */}
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:p-5 flex items-start gap-3" data-testid="admin-orphans-banner">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Emergent Object Storage: sem exclusão física</p>
          <p className="mt-1 leading-relaxed">
            A API atual do Emergent Object Storage <strong>não expõe endpoint de exclusão</strong> de objetos.
            Arquivos aqui listados permanecem no bucket, porém <strong>não são mais acessíveis</strong> pela UX pública
            (soft-delete no DB de origem). Uma migração futura para Cloudflare R2 ou AWS S3 habilitará a
            purga física via este mesmo painel. O adaptador de delete já está pronto.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid sm:grid-cols-4 gap-3">
          <SummaryCard label="Total registrados" value={summary.total} testid="admin-orphans-total" />
          <SummaryCard label="Pendentes de remoção física" value={summary.pending} tone="amber" testid="admin-orphans-pending" />
          <SummaryCard label="Reconhecidos (não removidos)" value={summary.acknowledged} tone="sky" testid="admin-orphans-acknowledged" />
          <SummaryCard label="Removidos fisicamente" value={summary.purged} tone="emerald" testid="admin-orphans-purged" />
        </div>
      )}

      {/* Actions */}
      <div className="bg-white border border-border rounded-2xl p-5 flex flex-wrap items-center gap-3">
        <p className="text-sm text-navy/70 mr-auto">Executar limpeza (com proteção contra arquivos ainda referenciados):</p>
        <button
          type="button"
          disabled={purging}
          onClick={() => runPurge(true)}
          data-testid="admin-orphans-dry-run-btn"
          className="inline-flex items-center gap-2 border border-border text-navy/80 hover:text-gold hover:border-gold px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors disabled:opacity-40"
        >
          <Play className="w-3.5 h-3.5" /> Simular limpeza
        </button>
        <button
          type="button"
          disabled={purging}
          onClick={() => runPurge(false)}
          data-testid="admin-orphans-purge-btn"
          className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors disabled:opacity-40"
          title="Executa a rotina de limpeza. Enquanto o storage não expuser DELETE, os registros ficam como reconhecidos."
        >
          <CheckCircle className="w-3.5 h-3.5" /> Reconhecer / Purgar
        </button>
        <button
          type="button"
          onClick={load}
          data-testid="admin-orphans-refresh-btn"
          className="w-9 h-9 rounded-full border border-border text-navy/70 hover:text-gold flex items-center justify-center"
          title="Atualizar"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {lastResult && (
        <div className="text-xs text-navy/70 bg-[hsl(var(--parchment))] border border-border rounded-lg p-3" data-testid="admin-orphans-last-result">
          Último {lastResult.dry_run ? "dry-run" : "purge"} em {lastResult.ranAt} ·
          scanned: <b>{lastResult.scanned}</b> ·
          would_delete: <b>{lastResult.would_delete}</b> ·
          deleted: <b>{lastResult.deleted}</b> ·
          still-referenced (protegidos): <b>{lastResult.still_referenced_skipped}</b> ·
          errors: <b>{lastResult.errors}</b>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <label className="text-xs text-navy/60">Filtrar status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-orphans-status-filter"
          className="text-xs border border-border rounded-lg px-2 py-1 bg-white"
        >
          <option value="">Todos</option>
          <option value="pending">Pendentes</option>
          <option value="acknowledged">Reconhecidos</option>
          <option value="purged">Purgados</option>
          <option value="failed">Falharam</option>
        </select>
      </div>

      {/* List */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="admin-orphans-list">
        {loading ? (
          <div className="p-6 text-sm text-navy/60">Carregando…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-navy/60" data-testid="admin-orphans-empty">
            Nenhum arquivo órfão registrado.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((it) => (
              <li key={it.id} data-testid={`admin-orphan-${it.id}`} className="p-4 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${STATUS_STYLE[it.status] || ""}`}>
                    {STATUS_LABELS[it.status] || it.status}
                  </span>
                  <span className="text-xs text-navy/55">{it.file_type}</span>
                  <span className="text-xs text-navy/40">·</span>
                  <span className="text-xs text-navy/55">{it.reason}</span>
                  <span className="text-xs text-navy/40 ml-auto">{it.created_at?.slice(0, 19).replace("T", " ")}</span>
                </div>
                <p className="mt-1 font-mono text-xs text-navy/80 break-all">{it.storage_path}</p>
                <div className="mt-1 flex items-center gap-3 text-xs text-navy/55">
                  <span>tentativas: {it.attempts}</span>
                  {it.last_error && <span className="text-rose-600">erro: {it.last_error}</span>}
                  {it.status === "pending" && (
                    <button
                      type="button"
                      onClick={() => acknowledgeOne(it.id)}
                      data-testid={`admin-orphan-ack-${it.id}`}
                      className="ml-auto text-navy/70 hover:text-gold text-[11px] uppercase tracking-widest"
                    >
                      Reconhecer
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function SummaryCard({ label, value, tone, testid }) {
  const cls = tone === "amber" ? "border-amber-300 bg-amber-50"
    : tone === "sky" ? "border-sky-300 bg-sky-50"
    : tone === "emerald" ? "border-emerald-300 bg-emerald-50"
    : "border-border bg-white";
  return (
    <div className={`rounded-2xl border p-4 ${cls}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">{label}</p>
      <p className="mt-1 text-3xl font-display text-navy">{value}</p>
    </div>
  );
}
